import React, { FC, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import { styles } from './styles';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { OnFormSubmitPayload } from '@core/FinalForm/typedefs';
import { useDispatch, useSelector } from 'react-redux';
import { keyframes } from '@emotion/react';
import { getScaleImage } from '@bus/profile/selectors';
import { profileActions } from '@bus/profile/actions';
import { modalActions } from '@core/Modal/state/actions';
import ImageGallery from '@components/icons/ImageGallery';
import Warning from '@components/icons/Warning';
import Visibility from '@components/icons/Visibility';
import useAspectRatio from '@routes/useAspectRatio';
import Logo from '@components/icons/Logo';

type MessageImageItemProps = {
  id: number | string;
  image: string;
  isError: boolean | string;
  ratio?: string | null;
  separator?: string;
  chatId: number;
  messageId: number;
  shortImage: string;
  isSwap: boolean;
  type: string;
  sagaAction?: ActionCreatorWithPayload<OnFormSubmitPayload>;
  isDeleted: boolean;
  isFetching?: boolean;
  isDelete?: boolean;
  isShare?: boolean;
};

export const MessageImageItem: FC<MessageImageItemProps> = ({
  id,
  image,
  isError,
  ratio = '16:9',
  separator = ':',
  chatId,
  messageId,
  shortImage,
  isSwap,
  type,
  sagaAction,
  isDeleted,
  isFetching = false,
  isDelete = false,
  isShare = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const scaleImage = useSelector(getScaleImage);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [aspectRatio] = useAspectRatio(image);

  const loading = keyframes`
    to {
      background-position-x: -20%;
    }
  `;

  const isLoading =
    !imageLoaded || (isFetching && id.toString().includes('mock'));

  const handleLoad = () => {
    setImageLoaded(true);
  };

  const openScale = (
    event: React.MouseEvent<HTMLDivElement>,
    isDeleted: boolean,
  ) => {
    if (isDeleted) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    !isLoading &&
      !scaleImage &&
      !isError &&
      dispatch(
        profileActions.setScaleImage({
          image,
          ratio: ratio ?? '16:9',
          chatId,
          short_image: shortImage,
          separator,
          isSwap,
          id,
          type,
          isShare,
          isDelete,
          handleDelete: () => {
            if (sagaAction) {
              new Promise((resolve, reject) => {
                dispatch(
                  sagaAction({
                    values: { id, chatId, messageId },
                    resolve,
                    reject,
                  }),
                );
              })
                .then(() => {
                  dispatch(modalActions.closeModal('ConfirmOrCancel'));
                  dispatch(profileActions.setScaleImage(null));
                })
                .catch();
            }
          },
        }),
      );
  };

  return (
    <Box
      sx={() => styles.messageImageItemSecond(aspectRatio as number)}
      id={id.toString()}>
      {!imageLoaded && image && !isError && !isDeleted && (
        <Box
          sx={[
            styles.imageMockWrapper,
            {
              animation: `1.5s ${loading} ease-in-out infinite`,
            },
          ]}>
          <Typography
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            variant={'body1'}
            color={'text.secondary'}>
            Visualizing
          </Typography>
        </Box>
      )}
      {isLoading && !image && !isError && !isDeleted && (
        <Box
          sx={[
            styles.imageMockWrapper,
            {
              animation: `1.5s ${loading} ease-in-out infinite`,
            },
          ]}>
          <Box sx={styles.loader}>
            <Logo sx={{ fontSize: '40px' }} />
            <Typography
              variant={'caption'}
              color={'text.disabled'}
              maxWidth={'150px'}
              textAlign={'center'}>
              This Image will be ready in the gallery soon
            </Typography>
          </Box>
        </Box>
      )}
      {isDeleted && (
        <Box sx={styles.imageMockWrapper}>
          <Box sx={styles.deletedImage}>
            <ImageGallery
              fontSize={'small'}
              htmlColor={theme.palette.text.secondary}
            />
            <Typography
              variant={'body1'}
              textAlign={'center'}
              color={'text.secondary'}>
              This image was deleted
            </Typography>
          </Box>
        </Box>
      )}
      {!!isError && (
        <Box sx={styles.imageErrorWrapper}>
          <Box>
            <Warning fontSize={'small'} htmlColor={theme.palette.error.main} />
            <Typography
              variant={'body1'}
              color={'text.secondary'}
              textAlign={'center'}>
              {typeof isError === 'string' ? isError : 'Please try again'}
            </Typography>
          </Box>
        </Box>
      )}
      <Box
        height={'100%'}
        sx={[styles.imageWrapper, isSwap ? styles.imageSwap : {}]}
        onClick={(event) => openScale(event, isDeleted)}>
        {image && (
          <Box
            width={'100%'}
            component={'img'}
            src={image}
            borderRadius={'10px'}
            onLoad={handleLoad}
          />
        )}
        {(!isLoading || !imageLoaded) && (
          <>
            <Box sx={styles.imageOverlay} />
            <Box sx={styles.eye}>
              <Visibility
                fontSize={'small'}
                htmlColor={theme.palette.common.white}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessageImageItem;
